import { useCallback, useRef, useState } from "react";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { IPoint, PdfViewerComponent } from "@syncfusion/ej2-react-pdfviewer";
import {
  DragAndDropEventArgs,
  FieldsSettingsModel,
  TreeViewComponent,
  TreeViewModel,
} from "@syncfusion/ej2-react-navigations";
import {
  MaskedTextBoxComponent,
  MaskedTextBoxModel,
} from "@syncfusion/ej2-react-inputs";
import {
  TemplateField,
  defaultTemplateFieldsConfig,
} from "./config/templateFieldsConfig";
import { deep } from "../../../utils/clone";
import { DataManager, Query, Predicate } from "@syncfusion/ej2-data";
import { first } from "lodash";
import { usePdfTemplateExpression } from "./usePdfTemplateExpression";
import { ExpressionData, AnnotationDefaults } from "../types/types";

export interface SearchResults {
  data: TemplateField[];
  expand: boolean;
}

export interface PdfTemplateDesignerExpressionToolbarModule {
  // Instance references
  maskedTextBoxInstance: MaskedTextBoxComponent | null;
  treeViewInstance: TreeViewComponent | null;
  templateFieldsConfig: TemplateField[];

  // Core functionality
  search: () => SearchResults | undefined;

  // Event handlers
  handleNodeDragStart: (args: DragAndDropEventArgs) => void;
  handleNodeDragStop: (args: DragAndDropEventArgs) => void;

  // Setup and lifecycle methods
  setup: (
    templateFields: ExpressionData[]
  ) => [
    MaskedTextBoxModel & DefaultHtmlAttributes,
    TreeViewModel & DefaultHtmlAttributes,
  ];
  initialize: (
    newMaskedTextBoxInstance: MaskedTextBoxComponent,
    newTreeViewInstance: TreeViewComponent
  ) => void;
  reset: () => void;
  setInstance: (
    scopeMaskedTextBoxInstance?: MaskedTextBoxComponent,
    scopeTreeViewInstance?: TreeViewComponent
  ) => void;

  // Configuration management
  setTemplateFieldsConfig: (expressionDataList: ExpressionData[]) => void;
}

export const usePdfTemplateDesignerExpressionToolbar = (
  treeViewRef: React.RefObject<TreeViewComponent | null>,
  maskedTextBoxRef: React.RefObject<MaskedTextBoxComponent | null>,
  pdfViewerRef: React.RefObject<PdfViewerComponent | null>,
  onPreviewAnnotations?: (data?: any, annotationsData?: string) => void,
  annotationDefaults?: AnnotationDefaults
) => {
  // Get the expression hook
  const { getDefaultExpressionAnnotationDimensions, addExpressionAnnotation } =
    usePdfTemplateExpression(pdfViewerRef, onPreviewAnnotations, annotationDefaults);

  // State
  const [maskedTextBoxInstance, setMaskedTextBoxInstance] =
    useState<MaskedTextBoxComponent | null>(null);
  const [treeViewInstance, setTreeViewInstance] =
    useState<TreeViewComponent | null>(null);
  const templateFieldsConfigRef = useRef<TemplateField[]>([]);

  const defaultMaskedTextBoxSettings: MaskedTextBoxModel &
    DefaultHtmlAttributes = {
    placeholder: "Search",
  };

  const defaultTreeViewSettings: TreeViewModel & DefaultHtmlAttributes = {
    allowDragAndDrop: true,
    fullRowSelect: false,
    fullRowNavigable: false,
  };

  // Datasource
  const changeDataSource = useCallback(
    (data: any) => {
      if (!treeViewRef.current) {
        return;
      }

      treeViewRef.current.fields = {
        dataSource: [...data],
        id: "id",
        text: "name",
        child: "fields",
      };
    },
    [treeViewRef.current]
  );

  const getItems = useCallback(
    (
      property: string,
      value: string,
      list?: TemplateField[],
      additionalProperty?: string
    ): TemplateField[] => {
      if (!(list && list.length > 0)) {
        return [];
      }
      const predicate = additionalProperty
        ? new Predicate(property, "contains", value, true).or(
            new Predicate(additionalProperty, "contains", value, true)
          )
        : new Predicate(property, "contains", value, true);

      const filteredList: any = new DataManager(list).executeLocal(
        new Query().where(predicate)
      );

      return filteredList;
    },
    []
  );

  const getItemById = useCallback(
    (id: string): TemplateField | undefined => {
      const results = deep<TemplateField[]>(templateFieldsConfigRef.current);

      results.forEach((element) => {
        const node = { ...element } as TemplateField;
        const r = getItems("id", id, node.fields);
        element.fields = node.fields?.filter((x: any) =>
          r.map((y) => y.id).includes(x.id)
        );
      });

      // Just get the first
      const category = first(
        results.filter((x) => x.fields && x.fields.length > 0)
      );
      const item = first(category?.fields);
      return item;
    },
    [templateFieldsConfigRef.current, getItems]
  );

  //search
  const search = useCallback((): SearchResults | undefined => {
    if (!maskedTextBoxRef.current) {
      return;
    }

    const _text = maskedTextBoxRef.current.element.value;

    if (_text == "") {
      // show all data
      changeDataSource([...templateFieldsConfigRef.current]);
      return { data: [...templateFieldsConfigRef.current], expand: false };
    }

    // Search children
    let results = deep<TemplateField[]>(templateFieldsConfigRef.current);

    results.forEach((element) => {
      const node = { ...element } as TemplateField;
      const r = getItems(
        "name",
        _text,
        node.fields,
        "templateExpression.expressionCategory"
      );
      element.fields = node.fields?.filter((x: any) =>
        r.map((y) => y.id).includes(x.id)
      );
    });

    results = results.filter((x) => x.fields && x.fields.length > 0);

    changeDataSource([...results]);
    return { data: [...results], expand: true };
  }, [
    maskedTextBoxRef.current,
    templateFieldsConfigRef.current,
    changeDataSource,
    getItems,
  ]);

  // Events
  const handleNodeDragStart = useCallback(
    (args: DragAndDropEventArgs) => {
      if (!treeViewRef.current) {
        return;
      }

      if (
        treeViewRef.current.selectedNodes.length <= 0 ||
        !args.draggedNodeData.parentID ||
        !treeViewRef.current.selectedNodes.includes(
          args.draggedNodeData.id as string
        )
      ) {
        // Either not selected or its just category or items trying to be dragged is not the one selected
        args.cancel = true;
      }
    },
    [treeViewRef.current]
  );

  const handleNodeDragging = useCallback(
    (args: DragAndDropEventArgs) => {
      if (!treeViewRef.current) {
        return;
      }

      if (
        args.target != null &&
        (args.target as HTMLElement).classList.contains("e-pv-text-layer")
      ) {
        if (!args.draggedNodeData.parentID) {
          // Its just category (revalidation)
          args.dropIndicator = "e-no-drop";
        } else {
          // All Good
          args.dropIndicator = "e-drop-in";
        }
      } else {
        // Not on PDF Page
        args.dropIndicator = "e-no-drop";
      }
    },
    [treeViewRef.current]
  );

  const handleNodeDragStop = useCallback(
    (args: DragAndDropEventArgs) => {
      if (
        !(
          args.target != null &&
          (args.target as HTMLElement).classList.contains("e-pv-text-layer")
        )
      ) {
        args.cancel = true;
      } else {
        if (!args.draggedNodeData.parentID) {
          // Its just the category (revalidation)
          return;
        }

        // get data
        const data = getItemById(args.draggedNodeData.id as string);

        if (!data?.templateExpression) {
          console.warn("Not an expression annotation.");
          return;
        }

        // Set coordinates
        const dimensions =
          data.templateExpression.expressionDimension ??
          getDefaultExpressionAnnotationDimensions(
            data.templateExpression.expressionType
          );
        const destinyPoint = {
          x: args.event.offsetX,
          y: args.event.offsetY,
        } as IPoint;
        const pointOffset = 50;

        const xPointCalc = destinyPoint.x + dimensions.width + pointOffset;
        if (xPointCalc >= args.target.clientWidth) {
          destinyPoint.x = Math.abs(destinyPoint.x - dimensions.width);
        }

        const yPointCalc = destinyPoint.y + dimensions.height + pointOffset;
        if (yPointCalc >= args.target.clientHeight) {
          destinyPoint.y = Math.abs(destinyPoint.y - dimensions.height);
        }

        // Add Expression
        addExpressionAnnotation(data.templateExpression, destinyPoint);
      }
    },
    [
      getItemById,
      getDefaultExpressionAnnotationDimensions,
      addExpressionAnnotation,
    ]
  );

  const setTemplateFieldsConfigAction = useCallback(
    (expressionDataList: ExpressionData[]) => {
      if (!(expressionDataList && expressionDataList.length > 0)) {
        templateFieldsConfigRef.current = [...defaultTemplateFieldsConfig];
        return;
      }

      // Set Fields
      const fieldsCategory = {
        id: "fields",
        name: "Fields",
        expanded: true,
        fields: [] as TemplateField[],
      } as TemplateField;

      // Map
      fieldsCategory.fields = expressionDataList.map((x) => {
        return {
          id: x.expressionName,
          name: x.expressionPlaceholder,
          templateExpression: x,
        } as TemplateField;
      });

      const config = [...defaultTemplateFieldsConfig, { ...fieldsCategory }];

      templateFieldsConfigRef.current = [...config];

      return [...config];
    },
    []
  );

  // Init
  const setup = (templateFields: ExpressionData[]) => {
    // TreeView Settings
    const treeViewSettings = {
      ...defaultTreeViewSettings,
    } as TreeViewModel & DefaultHtmlAttributes;

    const dataSource = setTemplateFieldsConfigAction(templateFields);

    treeViewSettings.nodeDragStart = handleNodeDragStart;
    treeViewSettings.nodeDragStop = handleNodeDragStop;
    treeViewSettings.nodeDragging = handleNodeDragging;
    treeViewSettings.fields = {
      dataSource: [...(dataSource || [])],
      id: "id",
      text: "name",
      child: "fields",
    } as unknown as FieldsSettingsModel;

    const maskedTextBoxModel = {
      ...defaultMaskedTextBoxSettings,
    } as MaskedTextBoxModel & DefaultHtmlAttributes;

    return [maskedTextBoxModel, treeViewSettings];
  };

  const initialize = useCallback(
    (
      newMaskedTextBoxInstance: MaskedTextBoxComponent,
      newTreeViewInstance: TreeViewComponent
    ) => {
      setInstance(newMaskedTextBoxInstance, newTreeViewInstance);
    },
    []
  );

  const reset = useCallback(() => {
    setInstance(undefined, undefined);
  }, []);

  const setInstance = useCallback(
    (
      scopeMaskedTextBoxInstance?: MaskedTextBoxComponent,
      scopeTreeViewInstance?: TreeViewComponent
    ) => {
      setMaskedTextBoxInstance(scopeMaskedTextBoxInstance || null);
      setTreeViewInstance(scopeTreeViewInstance || null);
    },
    []
  );

  return {
    maskedTextBoxInstance,
    treeViewInstance,
    templateFieldsConfig: templateFieldsConfigRef.current,
    search,
    handleNodeDragStart,
    handleNodeDragStop,
    setup,
    initialize,
    reset,
    setInstance,
    setTemplateFieldsConfig: setTemplateFieldsConfigAction,
  } as PdfTemplateDesignerExpressionToolbarModule;
};

export interface TemplateDesignerSetup {
  accessToken: string;
  fileDocument: any; // Changed from FileDocument to any since FileDocument import was removed
}
